import React, { useEffect, useRef, useState } from 'react';

import { Page, LayoutSingleColumn } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './ContactPage.module.css';

// ─── Click-to-copy email ────────────────────────────────────────────
// Renders the email address as a button (not an <a href="mailto:">)
// so clicking copies the address to the clipboard instead of opening
// the user's default mail client, which is often Apple Mail and not
// what users actually want. Falls back gracefully when the Async
// Clipboard API is unavailable (old browsers, non-secure contexts,
// or blocked permissions) by telling the user to copy manually —
// the email itself is still visible on screen as the button label.

const COPY_CONFIRM_MS = 2000;
const FALLBACK_MESSAGE_MS = 3000;

export const CopyableEmail = ({ email }) => {
  const [status, setStatus] = useState(null); // null | 'copied' | 'unsupported'
  const timerRef = useRef(null);

  // Clear pending timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const scheduleReset = ms => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setStatus(null), ms);
  };

  const handleCopy = async () => {
    const clipboard = typeof navigator !== 'undefined' ? navigator.clipboard : null;
    if (!clipboard || typeof clipboard.writeText !== 'function') {
      setStatus('unsupported');
      scheduleReset(FALLBACK_MESSAGE_MS);
      return;
    }
    try {
      await clipboard.writeText(email);
      setStatus('copied');
      scheduleReset(COPY_CONFIRM_MS);
    } catch (_) {
      // Permissions denied, focus missing, SecurityError, etc.
      setStatus('unsupported');
      scheduleReset(FALLBACK_MESSAGE_MS);
    }
  };

  const confirmText =
    status === 'copied'
      ? 'Copied!'
      : status === 'unsupported'
      ? 'Copy manually: press Cmd/Ctrl + C'
      : '';

  return (
    <div className={css.copyWrap}>
      <button
        type="button"
        className={css.copyBtn}
        aria-label={`Copy ${email} to clipboard`}
        onClick={handleCopy}
      >
        {email}
      </button>
      {/* Always rendered so it reserves layout space — prevents jump
          when the confirmation flashes in. aria-live ensures screen
          readers announce the "Copied!" on activation. */}
      <span
        className={`${css.copyConfirm} ${status ? css.copyConfirmVisible : ''}`}
        role="status"
        aria-live="polite"
      >
        {confirmText}
      </span>
    </div>
  );
};

// ─── Content ────────────────────────────────────────────────────────

const CATEGORY_CARDS = [
  {
    icon: '💬',
    iconClass: 'coral',
    title: 'General support',
    body:
      'Questions about a booking, your account, a listing, or anything technical.',
    email: 'support@rentmerrily.com',
  },
  {
    icon: '🔒',
    iconClass: 'yellow',
    title: 'Privacy & data',
    body:
      'Privacy questions and data requests. For California (CCPA) requests, use the subject line "California Privacy Request" so we can route it fast.',
    email: 'support@rentmerrily.com',
  },
  {
    icon: '📣',
    iconClass: 'green',
    title: 'Press & partnerships',
    body: 'Media inquiries, collabs, or working with Merrily.',
    email: 'hello@rentmerrily.com',
  },
];

// Easy to edit — add / remove questions here and they render in order.
const FAQ_ITEMS = [
  {
    q: 'How does renting work on Merrily?',
    a:
      'Browse kits near you, request to book the dates you want, and the host has 72 hours to accept or decline. Once accepted, you coordinate pickup with them through the app. No charge until the host accepts.',
    defaultOpen: true,
  },
  {
    q: 'What if the kit is damaged or missing items when returned?',
    a:
      "A security deposit is held at the time of booking. If anything's missing or damaged, hosts can open a claim within 48 hours of return, and our team helps mediate. We'll work with both sides to reach a fair resolution.",
  },
  {
    q: 'How do I list my own party supplies?',
    a:
      'Click "+ Add Kit" at the top of the page. You\'ll add photos, a description, a price per night, and your pickup location. Listings are reviewed before going live to make sure everything looks great.',
  },
  {
    q: 'How and when do I get paid as a host?',
    a:
      "Payouts start automatically the moment you confirm the kit's return. Stripe deposits them to your bank within 1–3 business days. You'll connect a bank account when you set up your first listing.",
  },
  {
    q: 'I forgot my password.',
    a:
      'Use the password reset link on the login page. Check your spam folder if the reset email does not show up within a few minutes.',
    answerNode: (
      <p>
        No problem. Use the{' '}
        <a href="/login" className={css.inlineLink}>
          password reset link on the login page
        </a>
        . Check your spam folder if the reset email doesn&apos;t show up within a
        few minutes.
      </p>
    ),
  },
  {
    q: 'Can I cancel a booking?',
    a:
      'Yes. Cancel 7+ days before pickup for a full refund; 3-7 days before, half the rental price is refunded; within 3 days the rental is non-refundable. Your deposit and delivery fees always come back in full. Your booking page shows exactly what you\'d get back if you cancel today.',
  },
];

// City-only until a business entity is set up with a real street address.
const MAILING_ADDRESS = 'Los Angeles, California';
const INSTAGRAM_HANDLE = '@rentmerrily';
const INSTAGRAM_URL = 'https://instagram.com/rentmerrily';

// ─── FAQ disclosure ─────────────────────────────────────────────────

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(!!item.defaultOpen);
  const contentId = `faq-a-${index}`;
  const buttonId = `faq-q-${index}`;
  return (
    <div className={open ? `${css.faqItem} ${css.faqItemOpen}` : css.faqItem}>
      <button
        type="button"
        id={buttonId}
        className={css.faqQuestion}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={() => setOpen(v => !v)}
      >
        <span>{item.q}</span>
        <span className={css.faqChev} aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div id={contentId} role="region" aria-labelledby={buttonId} className={css.faqAnswer}>
          {item.answerNode || <p>{item.a}</p>}
        </div>
      )}
    </div>
  );
};

// ─── Page ───────────────────────────────────────────────────────────

const ContactPage = () => {
  return (
    <Page
      title="Contact"
      description="Questions, feedback, or something broken? Here are the best ways to reach the Merrily team."
      className={css.root}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.container}>
          {/* Hero */}
          <section className={css.hero}>
            <h1 className={css.heroTitle}>Let&apos;s talk</h1>
            <p className={css.heroLede}>
              Questions, feedback, or something broken? We&apos;re a small team and we actually
              read every message. Here&apos;s the best way to reach us.
            </p>
          </section>

          {/* Category cards */}
          <section className={css.categoryGrid} aria-label="Contact categories">
            {CATEGORY_CARDS.map(card => (
              <article key={card.title} className={css.categoryCard}>
                <div
                  className={`${css.iconWrap} ${css[`iconWrap_${card.iconClass}`]}`}
                  aria-hidden="true"
                >
                  {card.icon}
                </div>
                <h2 className={css.categoryTitle}>{card.title}</h2>
                <p className={css.categoryBody}>{card.body}</p>
                <CopyableEmail email={card.email} />
              </article>
            ))}
          </section>

          {/* FAQ */}
          <section className={css.faqSection}>
            <header className={css.faqHeader}>
              <h2 className={css.faqHeaderTitle}>Common questions</h2>
              <p className={css.faqHeaderBody}>You might find a faster answer here.</p>
            </header>

            <div className={css.faqList}>
              {FAQ_ITEMS.map((item, idx) => (
                <FaqItem key={item.q} item={item} index={idx} />
              ))}
            </div>
          </section>

          {/* Social */}
          <section className={css.socialSection}>
            <h2 className={css.socialTitle}>Find us elsewhere</h2>
            <p className={css.socialBody}>
              Follow along for party inspiration, new kits, and behind-the-scenes from the Merrily
              team.
            </p>
            <div className={css.socialLinks}>
              <a
                href={INSTAGRAM_URL}
                className={css.socialLink}
                target="_blank"
                rel="noopener noreferrer"
              >
                📷 {INSTAGRAM_HANDLE}
              </a>
            </div>
          </section>

          {/* Mailing address */}
          <footer className={css.mailingFooter}>
            <strong>Mailing address</strong>
            {MAILING_ADDRESS}
          </footer>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default ContactPage;
